import {
  Disc3,
  FileSpreadsheet,
  History,
  IdCard,
  Inbox,
  Layers,
  LayoutGrid,
  ListChecks,
  MonitorSmartphone,
  UserCheck,
  UserPlus,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { Role } from '@/api/types'

// 내비게이션은 두 층이다. **레일이 대분류(섹션)**, **사이드바가 그 섹션의 상세 메뉴**다.
// 한 배열을 둘이 함께 그리면 한쪽을 누를 때 다른 쪽이 따라 움직인다 — 그것이 층이 없다는 뜻이다.
//
// 여기 있는 것은 전부 열리는 화면이다. 없는 화면은 여기에도 없다.

export type Item = {
  to: string
  label: string
  icon: LucideIcon
  /** 상세 경로까지 포함해서 활성인지 본다. */
  match: (path: string) => boolean
  /** 최고 관리자의 것(F-38). 플랫폼 자체를 다루는 화면이다. */
  top?: true
}

export type SectionId = 'home' | 'people' | 'vms' | 'install'

export type Section = {
  id: SectionId
  label: string
  icon: LucideIcon
  items: Item[]
  /** 최고 관리자의 것(F-38). */
  top?: true
}

const under = (prefix: string) => (p: string) => p === prefix || p.startsWith(`${prefix}/`)

export const SECTIONS: Section[] = [
  {
    id: 'home',
    label: '대시보드',
    icon: LayoutGrid,
    top: true,
    items: [
      { to: '/', label: '검토 대기', icon: Inbox, match: (p) => p === '/' },
      // 클러스터의 상세 화면은 없다 — 조작은 카드의 메뉴에 있다. 노드 상세(`/nodes/:id`)는 이 화면의 노드 카드에서 들어가므로 여기 속한다.
      { to: '/clusters', label: '클러스터', icon: Layers, match: (p) => under('/clusters')(p) || under('/nodes')(p) },
      { to: '/activity', label: '작업 내역', icon: History, match: under('/activity') },
      { to: '/reports', label: '실적', icon: FileSpreadsheet, match: under('/reports') },
    ],
  },
  {
    id: 'people',
    label: '사용자',
    icon: Users,
    items: [
      { to: '/users', label: '사용자', icon: UserCheck, match: under('/users') },
      { to: '/signups', label: '가입 관리', icon: UserPlus, match: under('/signups'), top: true },
      { to: '/teams', label: '팀', icon: Users, match: under('/teams') },
      { to: '/quota-tiers', label: '직위', icon: IdCard, match: under('/quota-tiers'), top: true },
    ],
  },
  {
    id: 'vms',
    label: 'VM',
    icon: MonitorSmartphone,
    items: [
      { to: '/vms', label: 'VM', icon: MonitorSmartphone, match: under('/vms') },
      { to: '/catalog', label: '카탈로그', icon: ListChecks, match: under('/catalog'), top: true },
    ],
  },
  {
    id: 'install',
    label: '설치',
    icon: Disc3,
    top: true,
    items: [{ to: '/install/iso', label: 'ISO', icon: Disc3, match: under('/install/iso') }],
  },
]

const FALLBACK = SECTIONS[0]!

/** 지금 어느 섹션에 있는가. 레일의 활성 표시와 사이드바의 내용이 둘 다 이것 하나를 본다. */
export function sectionOf(pathname: string): Section {
  return SECTIONS.find((s) => s.items.some((i) => i.match(pathname))) ?? FALLBACK
}

/**
 * 그 사람에게 열리는 메뉴(F-38). 최고 관리자는 전부, 관리자는 사람·팀·VM 뿐이다 —
 * 플랫폼 자체(검토 대기·클러스터·작업 내역·카탈로그·ISO)는 최고 관리자의 것이다.
 * 직위도 그쪽이다 — 한도의 숫자가 전역이라 고치면 자기 팀 밖 사람의 한도가 함께 움직인다.
 */
export function sectionsFor(role: Role): Section[] {
  if (role === 'superadmin') return SECTIONS
  return SECTIONS.filter((s) => !s.top).map((s) => ({ ...s, items: s.items.filter((i) => !i.top) }))
}

/** 로고와 대시보드가 여는 곳. 관리자에게는 대시보드가 없다. */
export function homeFor(role: Role, pending: number): string {
  if (role !== 'superadmin') return '/vms'
  return pending > 0 ? '/' : '/clusters'
}
